import React, { FC, useEffect, useRef } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { reopenSelectionWarning } from "../helper";
import { RosterAction } from "../../../utils/selectionRoster";

interface Props {
    expertName: string;
    action: RosterAction;
    siblingProjects?: Array<{ id: number; title?: string }>;
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

const ACTION_BUTTON =
    "w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-all disabled:cursor-not-allowed";
const STEP_PILL =
    "font-circular text-[10.5px] font-bold uppercase tracking-[0.4px] text-[#8A6A2F] bg-white border border-[#EDE3D3] rounded-full px-2 py-[1px] whitespace-nowrap " +
    "dark:text-accent-text dark:bg-surface-2 dark:border-border-token";

export const ReopenSelectionNotice: FC<Props> = ({
    expertName,
    action,
    siblingProjects = [],
    busy = false,
    onConfirm,
    onCancel,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const { headline, detail, steps } = reopenSelectionWarning(
        expertName,
        siblingProjects,
        action
    );

    useEffect(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [expertName, action]);

    return (
        <div
            ref={ref}
            className="flex items-center gap-3 p-4 mb-4 rounded-[16px] border border-[#E8DCC8] bg-[#FDF9F3] shadow-[0_6px_20px_rgba(122,92,36,0.07)] dark:border-border-token dark:bg-tint dark:shadow-none"
        >
            <div className="w-[34px] h-[34px] shrink-0 rounded-full bg-primaryLight text-primary dark:bg-[#7A5B12]/30 dark:text-accent-text flex items-center justify-center">
                <WarningAmberRoundedIcon sx={{ fontSize: 19, display: "block" }} />
            </div>

            <div className="flex-1 min-w-0">
                <div className="font-circular text-[14.5px] font-bold text-text-1 leading-[1.35]">
                    {headline}
                </div>
                <div className="font-circular text-[12.5px] text-text-2 leading-[1.6] mt-1">
                    {detail}
                </div>
                <div className="flex items-center flex-wrap gap-[6px] mt-[10px]">
                    {steps.map((step, index) => (
                        <React.Fragment key={step}>
                            {index > 0 && (
                                <span className="text-[12px] text-[#CBB693] dark:text-accent-text">›</span>
                            )}
                            <span className={STEP_PILL}>{step}</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-2">
                <Tooltip
                    arrow
                    title={
                        action === "remove"
                            ? "Yes, remove and reopen"
                            : "Yes, add and reopen"
                    }
                >
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onConfirm}
                        className={`${ACTION_BUTTON} bg-primary text-white shadow-[0_2px_8px_rgba(122,92,36,0.28)] hover:bg-[#8C7041] disabled:bg-[#DCCDB4] dark:shadow-none dark:hover:bg-accent-dark dark:disabled:bg-track dark:disabled:text-muted`}
                    >
                        {busy ? (
                            <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
                        ) : (
                            <CheckIcon sx={{ fontSize: 19 }} />
                        )}
                    </button>
                </Tooltip>
                <Tooltip arrow title="No, keep it as it is">
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onCancel}
                        className={`${ACTION_BUTTON} bg-white text-[#7A5C24] border border-[#E0D3BC] hover:bg-primaryLight dark:bg-surface-2 dark:text-accent-text dark:border-border-token dark:hover:bg-tint`}
                    >
                        <CloseIcon sx={{ fontSize: 19 }} />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

export default ReopenSelectionNotice;
